/**
 * EndUserTopBar — the ONE end-user application top bar, shared by the
 * platform's Run view (7077) and the standalone runtime client.
 *
 * Composes RuntimeTopBar with the end-user chrome 7077 renders today:
 *   left  — app icon + title, then the Apps pill (app switcher)
 *   right — Search, Approvals pill (+ pending badge),
 *           a host-provided sites slot, then the user avatar menu with the
 *           light/dark theme toggle and "Project settings" entries.
 *
 * Every capability is injected by the host, so this file carries no store /
 * router / http dependency:
 *   - `onOpenSearch` absent → Search button hidden
 *   - `onOpenApps` absent   → Apps button hidden
 *   - `approvals` absent    → Approvals button hidden (Phase 6 wires the
 *                             standalone runtime's inbox)
 *   - `theme` absent        → no theme-toggle menu entry
 *   - `onOpenProjectSettings` absent → no "Project settings" menu entry
 *   - `support` absent      → no Support entries. Present, Support lives in
 *                             the USER MENU ("Support" with what is waiting
 *                             on you, "Report an issue"), and the avatar
 *                             carries the waiting count.
 */
import type { CSSProperties, ReactNode } from 'react'
import { Bug, ChevronDown, Inbox, LayoutGrid, LifeBuoy, Search, SlidersHorizontal } from 'lucide-react'
import { useShellChrome } from './nav/chromeContext'
import RuntimeTopBar from './RuntimeTopBar'
import { useSupport, type SupportButtonProps } from '../support/SupportButton'
import type { UserMenuItem } from './UserMenu'
import type { AdminExtraTab } from './AdminConsole'
import type { ShellApi, ShellApp, ShellCapabilities, ShellIdentity, ShellNotify } from './types'

export interface EndUserTopBarProps {
  app: ShellApp
  identity: ShellIdentity | null
  api: ShellApi
  /** Defaults to no in-shell admin console ({users:false, access:false}). */
  caps?: ShellCapabilities
  adminExtraTabs?: AdminExtraTab[]
  /** Opens the host's app switcher; absent → Apps button hidden. */
  onOpenApps?: () => void
  /** Other applications on offer — shown as a small count on the Apps button. */
  appsCount?: number
  /**
   * Opens application-wide search (records + document content). Absent → the
   * Search button is hidden, which is the correct state for a surface that
   * mounts no search panel: a button that opens nothing is worse than none.
   */
  onOpenSearch?: () => void
  /**
   * The "waiting on you" button: count + opener, absent → button hidden.
   * `label`/`title` let a host that opens the unified worklist say so —
   * the platform Run shell shows "Work" because approvals are only one
   * filter inside its panel, while a host still wired to the approvals
   * inbox alone keeps the default wording.
   */
  approvals?: { count: number | null; onOpen: () => void; label?: string; title?: string }
  /**
   * The Support module — report an issue, follow your issues, and (support
   * team) jump to the board — offered from the user menu. Absent → hidden.
   * Every prop goes straight to the shared `useSupport`, so a host binds the
   * transport (`runtime`) and its own navigation (`onOpenBoard`,
   * `onOpenStaging`); `openRequest` still opens an issue directly.
   */
  support?: SupportButtonProps
  /** Host-rendered site link(s), e.g. the platform's SitesButton. */
  sitesSlot?: ReactNode
  /** Light / dark — the user menu's Appearance choice (the ONLY theme control
   *  in the running application). */
  theme?: { value: 'light' | 'dark'; onToggle: () => void }
  /** "Project settings" (prompt-variable review) user-menu entry. */
  onOpenProjectSettings?: () => void
  /** Sign-in affordance shown when there is no identity. */
  onSignIn?: () => void
  /** Click on the app icon + title (e.g. navigate home). */
  onTitleClick?: () => void
  /** Additional host-specific user-menu entries (rendered after the built-ins). */
  menuExtraItems?: UserMenuItem[]
  /** Host toast adapter (the platform injects its toast library). */
  notify?: ShellNotify
}

/**
 * Top-bar buttons are quiet until pointed at: no border, no fill, and a soft
 * tint of the app's accent on hover/press. Inline styles cannot express :hover,
 * so the effect lives in this one stylesheet (`!important` because the base
 * look is inline). The Apps pill is tinted at rest, since it is a destination
 * switcher rather than a plain action. `NotificationBell` takes `tw-tb-icon`.
 */
const TOP_BAR_CSS = `
.tw-tb,.tw-tb-icon{transition:background-color .15s,color .15s,box-shadow .15s,transform .1s}
.tw-tb:hover,.tw-tb-icon:hover{background:color-mix(in srgb,var(--color-accent,#2563eb) 12%,transparent) !important;color:var(--color-accent,#2563eb) !important}
.tw-tb:active,.tw-tb-icon:active{transform:scale(.96)}
.tw-tb:focus-visible,.tw-tb-icon:focus-visible{outline:2px solid var(--color-accent,#2563eb);outline-offset:2px}
.tw-apps:hover{background:color-mix(in srgb,var(--color-accent,#2563eb) 22%,transparent) !important;box-shadow:0 2px 8px color-mix(in srgb,var(--color-accent,#2563eb) 25%,transparent)}
@media (prefers-reduced-motion:reduce){.tw-tb,.tw-tb-icon{transition:none}.tw-tb:active,.tw-tb-icon:active{transform:none}}
`

const ghostBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0.45rem 0.8rem',
  borderRadius: 10, fontWeight: 500, fontSize: '0.875rem',
  background: 'transparent', color: 'var(--color-text, #0f172a)',
  border: '1px solid transparent', cursor: 'pointer',
}

const appsBtn: CSSProperties = {
  ...ghostBtn, gap: 8, padding: '0.4rem 0.75rem 0.4rem 0.6rem', borderRadius: 999, fontWeight: 600,
  background: 'color-mix(in srgb, var(--color-accent, #2563eb) 12%, transparent)',
  color: 'var(--color-accent, #2563eb)',
  border: '1px solid color-mix(in srgb, var(--color-accent, #2563eb) 28%, transparent)',
}

export default function EndUserTopBar({
  app, identity, api, caps, adminExtraTabs,
  onOpenApps, appsCount, onOpenSearch, approvals, support, sitesSlot, theme, onOpenProjectSettings,
  onSignIn, onTitleClick, menuExtraItems, notify,
}: EndUserTopBarProps) {
  const effCaps: ShellCapabilities = caps ?? { users: false, access: false, dataAdminUrl: null }
  // Support — only with an identity: an anonymous visitor has no issues to
  // follow and the endpoints would refuse the report.
  const sup = useSupport(support && identity ? { ...support, notify } : null)
  // What the Run shell's active layout puts in this bar (its menus, the
  // Layout choice, full screen) — nothing without a ShellChromeProvider.
  const chrome = useShellChrome()

  // Built-in user-menu entries in 7077's order: Project settings, then any
  // host extras. Appearance (light / dark) and Layout are the menu's own
  // choices at its top.
  const items: UserMenuItem[] = []
  if (onOpenProjectSettings) {
    items.push({
      label: 'Project settings',
      icon: <SlidersHorizontal size={16} />,
      onClick: onOpenProjectSettings,
    })
  }
  if (support && identity) {
    items.push({
      label: support.label || 'Support',
      icon: <LifeBuoy size={16} />,
      onClick: sup.openPanel,
      badge: sup.badge,
      title: sup.title,
    })
    items.push({
      label: 'Report an issue',
      icon: <Bug size={16} />,
      onClick: sup.openReport,
      title: 'Report a problem or request a feature',
    })
  }
  if (menuExtraItems) items.push(...menuExtraItems)

  // Full screen: the shell hides every piece of chrome, this bar included.
  if (chrome.hidden) return null

  return (
    <RuntimeTopBar
      app={app}
      identity={identity}
      api={api}
      caps={effCaps}
      adminExtraTabs={adminExtraTabs}
      onTitleClick={onTitleClick}
      notify={notify}
      look={{
        appearance: theme ? {
          value: theme.value,
          onChange: (v) => { if (v !== theme.value) theme.onToggle() },
        } : undefined,
        layout: chrome.layout,
        beforeSignOut: chrome.beforeSignOut,
        afterSignOut: chrome.afterSignOut,
      }}
      centerExtra={chrome.center}
      leftExtra={onOpenApps || chrome.left ? (
        <>
          {/* App switcher — jump to another application the user can access,
              choosing Run / Workbench / Builder per their rights there. */}
          {onOpenApps && (
            <button
              type="button"
              onClick={onOpenApps}
              className="tw-tb tw-apps"
              title="Open another application"
              style={appsBtn}
            >
              <LayoutGrid size={15} />
              Apps
              {typeof appsCount === 'number' && appsCount > 0 && (
                <span
                  aria-label={`${appsCount} other application${appsCount === 1 ? '' : 's'}`}
                  style={{
                    minWidth: 18, height: 18, padding: '0 5px',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1,
                    borderRadius: 9, background: 'var(--color-bg, #ffffff)',
                    color: 'var(--color-accent, #2563eb)',
                  }}
                >
                  {appsCount > 99 ? '99+' : appsCount}
                </span>
              )}
              <ChevronDown size={12} strokeWidth={3} aria-hidden="true" />
            </button>
          )}
          {chrome.left}
        </>
      ) : undefined}
      rightExtra={(
        <>
          <style>{TOP_BAR_CSS}</style>
          {chrome.right}
          {/* Search — one query across the records and documents this profile
              may see. First in the group because it is the most-used control
              in the bar and the one a reader reaches for without looking. */}
          {onOpenSearch && (
            <button
              type="button"
              onClick={onOpenSearch}
              className="tw-tb"
              title="Search (Ctrl+K)"
              style={ghostBtn}
            >
              <Search size={15} />
              Search
            </button>
          )}
          {/* Global approvals — pending sign-offs across every screen. */}
          {approvals && (
            <button
              type="button"
              onClick={approvals.onOpen}
              title={approvals.title
                || 'Approvals — records across all screens pending your sign-off'}
              className="tw-tb"
              style={{ ...ghostBtn, position: 'relative' }}
            >
              <Inbox size={15} />
              {approvals.label || 'Approvals'}
              {typeof approvals.count === 'number' && approvals.count > 0 && (
                <span style={{
                  minWidth: 18, height: 18, padding: '0 5px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1,
                  borderRadius: 9, background: 'var(--color-attention, #c2410c)', color: '#fff',
                }}>
                  {approvals.count > 99 ? '99+' : approvals.count}
                </span>
              )}
            </button>
          )}
          {/* Support's panel + report dialog (opened from the user menu). */}
          {sup.ui}
          {/* The app's site(s) open in a new tab from inside the running app. */}
          {sitesSlot}
        </>
      )}
      onSignIn={onSignIn}
      menuExtraItems={identity && items.length ? items : undefined}
    />
  )
}
